import React, { useState } from 'react';
import { Outlet, Link, useLocation } from 'react-router-dom';
import {
  Box,
  Drawer,
  AppBar,
  Toolbar,
  List,
  Typography,
  Divider,
  IconButton,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Tour as TourIcon,
  Devices as DevicesIcon,
  PermMedia as MediaIcon,
  Public as WorldIcon,
  Logout as LogoutIcon,
  Groups as GroupsIcon,
  BarChart as BarChartIcon,
  SettingsRemote as RemoteIcon,
  SmartToy as SmartToyIcon,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';

const drawerWidth = 240;

const Layout: React.FC = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();
  const { logout } = useAuth();

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const menuItems = [
    { text: 'Tour', icon: <TourIcon />, path: '/tours' },
    { text: 'Dispositivi', icon: <DevicesIcon />, path: '/devices' },
    { text: 'Telefoni app', icon: <RemoteIcon />, path: '/app-installations' },
    { text: 'File Multimediali', icon: <MediaIcon />, path: '/media' },
    { text: 'Mondi Virtuali', icon: <WorldIcon />, path: '/virtual-worlds' },
    { text: 'Statistiche Gruppi', icon: <GroupsIcon />, path: '/group-stats' },
    { text: 'Statistiche Tour', icon: <BarChartIcon />, path: '/tour-stats' },
    { text: 'Assistente AI', icon: <SmartToyIcon />, path: '/ai-assistant' },
  ];

  const drawer = (
    <div>
      <Toolbar
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          py: 2,
          borderBottom: '1px solid rgba(212, 175, 55, 0.3)',
        }}
      >
        <Box
          component="img"
          src="/logoHistoria.png"
          alt="Historia"
          sx={{
            maxWidth: '180px',
            height: 'auto',
            filter: 'drop-shadow(0 2px 4px rgba(139, 69, 19, 0.3))',
          }}
        />
      </Toolbar>
      <Divider />
      <List>
        {menuItems.map((item) => (
          <ListItem key={item.text} disablePadding>
            <ListItemButton
              component={Link}
              to={item.path}
              selected={location.pathname.startsWith(item.path)}
              sx={{
                '&.Mui-selected': {
                  borderLeft: '4px solid #D4AF37',
                },
              }}
            >
              <ListItemIcon sx={{ color: '#8B4513' }}>{item.icon}</ListItemIcon>
              <ListItemText primary={item.text} />
            </ListItemButton>
          </ListItem>
        ))}
      </List>
      <Divider />
      <List>
        <ListItem disablePadding sx={{ py: 1 }}>
          <Typography variant="overline" sx={{ px: 3, fontWeight: 'bold', color: '#8B4513' }}>
            Altre Esperienze
          </Typography>
        </ListItem>
        <ListItem disablePadding>
          <ListItemButton
            component={Link}
            to="/civita-control"
            selected={location.pathname.startsWith('/civita-control')}
            sx={{
              '&.Mui-selected': {
                borderLeft: '4px solid #D4AF37',
              },
            }}
          >
            <ListItemIcon sx={{ color: '#8B4513' }}><RemoteIcon /></ListItemIcon>
            <ListItemText primary="Controllo Civita" />
          </ListItemButton>
        </ListItem>
      </List>
      <Divider />
      <List>
        <ListItem disablePadding>
          <ListItemButton onClick={logout}>
            <ListItemIcon sx={{ color: '#8B4513' }}>
              <LogoutIcon />
            </ListItemIcon>
            <ListItemText primary="Esci" />
          </ListItemButton>
        </ListItem>
      </List>
    </div>
  );

  return (
    <Box sx={{ display: 'flex' }}>
      <AppBar
        position="fixed"
        sx={{
          width: { sm: `calc(100% - ${drawerWidth}px)` },
          ml: { sm: `${drawerWidth}px` },
        }}
      >
        <Toolbar>
          <IconButton
            color="inherit"
            aria-label="apri menu"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2, display: { sm: 'none' } }}
          >
            <MenuIcon />
          </IconButton>
          <Typography variant="h6" noWrap component="div">
            {menuItems.find((item) => location.pathname.startsWith(item.path))?.text || 'Historia Manager'}
          </Typography>
        </Toolbar>
      </AppBar>
      <Box
        component="nav"
        sx={{ width: { sm: drawerWidth }, flexShrink: { sm: 0 } }}
        aria-label="menu navigazione"
      >
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true,
          }}
          sx={{
            display: { xs: 'block', sm: 'none' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: drawerWidth,
            },
          }}
        >
          {drawer}
        </Drawer>
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', sm: 'block' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: drawerWidth,
            },
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 3,
          width: { sm: `calc(100% - ${drawerWidth}px)` },
        }}
      >
        <Toolbar />
        <Outlet />
      </Box>
    </Box>
  );
};

export default Layout;
